import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSpark } from "@/lib/store";
import { submitCareerPulse, respondReflection, getProfile, patchProfile } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { TalkInput } from "@/components/TalkInput";

const DIMENSIONS = [
  { key: "meaning", label: "Meaning", note: "How much does the work matter to you?" },
  { key: "energy", label: "Energy", note: "Do you leave work with energy left over?" },
  { key: "challenge", label: "Challenge", note: "Are you stretched in useful ways?" },
  { key: "growth", label: "Growth", note: "Are you learning?" },
  { key: "autonomy", label: "Autonomy", note: "How much say do you have in how the work happens?" },
  { key: "relationships", label: "Relationships", note: "The people around you — colleagues, leaders, clients." },
  { key: "leadership", label: "Leadership", note: "The people you're working under and beside." },
  { key: "recognition", label: "Recognition", note: "Is your contribution seen?" },
  { key: "reward", label: "Reward", note: "The money side — fair enough?" },
  { key: "flexibility", label: "Flexibility", note: "How the work fits with the rest of your life." },
  { key: "wellbeing", label: "Wellbeing", note: "How the work leaves you, physically and mentally." },
  { key: "confidence", label: "Confidence", note: "How you feel about yourself in this work." },
];

// STEP 1: rating grid. STEP 2: probes for notable scores. STEP 3: coaching reflection.
export default function CareerPulse() {
  const { userId, profile, setProfile } = useSpark();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [scores, setScores] = useState(() => {
    const seeded = profile?.career_pulse?.scores || {};
    return Object.fromEntries(DIMENSIONS.map(d => [d.key, seeded[d.key] ?? 5]));
  });
  const [probes, setProbes] = useState(() => profile?.career_pulse?.probes || {});
  const [submitting, setSubmitting] = useState(false);
  const [reflection, setReflection] = useState(null);
  const [mode, setMode] = useState(null); // partly | no
  const [correction, setCorrection] = useState("");

  const notable = useMemo(
    () => DIMENSIONS.filter(d => scores[d.key] <= 4 || scores[d.key] >= 8),
    [scores]
  );

  const setScore = (k, v) => setScores(s => ({ ...s, [k]: parseInt(v) }));
  const setProbe = (k, v) => setProbes(p => ({ ...p, [k]: v }));

  const goToProbes = () => setStep(notable.length ? 2 : 3);
  const goToReflection = async () => {
    setSubmitting(true);
    try {
      const r = await submitCareerPulse(userId, { scores, probes });
      setReflection(r);
      setStep(3);
    } catch (e) {
      toast("Something interrupted us. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const respond = async (status) => {
    if (!reflection) return;
    if (status !== "yes" && !correction.trim()) { setMode(status); return; }
    try {
      const updated = await respondReflection(reflection.id, { status, correction });
      setReflection(updated);
      setMode(null);
      setCorrection("");
      let p = await getProfile(userId);
      if (status === "yes" && !p.completed_activities?.includes("career-pulse")) {
        p = await patchProfile(userId, "completed_activities", [...(p.completed_activities || []), "career-pulse"]);
      }
      setProfile(p);
      if (status === "yes") toast("Held in your Career Compass.");
      else if (status === "partly") toast("Recalibrated with your correction.");
      else toast("Set aside. Spark won't build on this one.");
    } catch (e) {
      toast(e?.response?.data?.detail || "Something interrupted us. Please try again.");
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <button data-testid="pulse-back-btn" onClick={() => nav("/part-one")} className="text-sm text-[#8A8A91] hover:text-[#17171C] inline-flex items-center gap-1.5 mb-6">
        <ArrowLeft size={14} /> My Journey
      </button>

      <p className="spark-eyebrow-copper spark-eyebrow-with-dot">Understand me</p>
      <h1 className="mt-2 text-4xl sm:text-5xl leading-tight text-[#17171C] font-medium tracking-tight">Career Pulse</h1>
      <p className="mt-4 text-lg text-[#56565D] max-w-2xl">
        A quiet check across twelve parts of working life. Move each slider to where things sit
        for you right now.
      </p>

      {step === 1 && (
        <div className="mt-10 space-y-6">
          {DIMENSIONS.map(d => (
            <div key={d.key} className="spark-card p-6">
              <div className="flex items-baseline justify-between">
                <div>
                  <p className="text-xl text-[#17171C]">{d.label}</p>
                  <p className="text-sm text-[#8A8A91] mt-1">{d.note}</p>
                </div>
                <span data-testid={`pulse-${d.key}-value`} className="text-3xl text-[#2C50A0] tabular-nums">{scores[d.key]}</span>
              </div>
              <input
                type="range" min={0} max={10} step={1}
                value={scores[d.key]}
                onChange={e => setScore(d.key, e.target.value)}
                data-testid={`pulse-${d.key}-slider`}
                className="mt-4 w-full accent-[#2C50A0]"
              />
              <div className="flex justify-between text-xs text-[#8A8A91] mt-1">
                <span>Low</span><span>High</span>
              </div>
            </div>
          ))}

          <div className="flex justify-between pt-2">
            <span />
            <button data-testid="pulse-continue-btn" onClick={goToProbes} className="spark-btn-primary">Continue</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="mt-10 space-y-6">
          <p className="spark-eyebrow">The scores that stood out</p>
          <p className="spark-voice text-2xl leading-snug text-[#17171C] max-w-2xl">
            A number on its own doesn't say much. What's behind these?
          </p>
          {notable.map(d => (
            <div key={d.key} className="spark-card p-6">
              <div className="flex items-baseline justify-between">
                <p className="text-xl text-[#17171C]">{d.label}</p>
                <span className="text-2xl text-[#2C50A0]">{scores[d.key]}</span>
              </div>
              <TalkInput
                testid={`pulse-probe-${d.key}`}
                rows={3}
                value={probes[d.key] || ""}
                onChange={v => setProbe(d.key, v)}
                placeholder="What's behind that score?"
                className="mt-4"
              />
            </div>
          ))}
          <div className="flex justify-between pt-2">
            <button onClick={() => setStep(1)} className="spark-btn-secondary">Back to scores</button>
            <button data-testid="pulse-generate-reflection-btn" onClick={goToReflection} disabled={submitting} className="spark-btn-primary disabled:opacity-60">
              {submitting ? "Spark is reading between the lines…" : "See what Spark notices"}
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="mt-10 space-y-6">
          {!reflection ? (
            <div className="spark-card p-8">
              <p className="text-[#56565D]">Preparing your reflection…</p>
              <button onClick={goToReflection} className="spark-btn-secondary mt-4">Try again</button>
            </div>
          ) : (
            <div className="spark-voice-block">
              <div>
                <p className="spark-eyebrow-copper mb-2">What I'm noticing</p>
                <p data-testid="pulse-reflection-notice" className="spark-voice text-2xl leading-snug text-[#17171C]">{reflection.notice}</p>

                <p className="spark-eyebrow mt-8 mb-2">The evidence</p>
                <p data-testid="pulse-reflection-evidence" className="text-[#56565D] leading-relaxed max-w-2xl">{reflection.evidence}</p>

                <p className="spark-eyebrow-copper mt-8 mb-2">One possibility</p>
                <p data-testid="pulse-reflection-interpretation" className="spark-voice text-xl leading-snug" style={{ color: "#1F3D80" }}>{reflection.interpretation}</p>

                {reflection.status === "pending" && !mode && (
                  <>
                    <p className="mt-6 text-sm text-[#8A8A91]">Does that feel accurate?</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button data-testid="pulse-yes-btn" onClick={() => respond("yes")} className="spark-btn-copper">Yes</button>
                      <button data-testid="pulse-partly-btn" onClick={() => setMode("partly")} className="spark-btn-secondary">Partly</button>
                      <button data-testid="pulse-no-btn" onClick={() => setMode("no")} className="spark-btn-secondary">No</button>
                    </div>
                  </>
                )}

                {mode && reflection.status === "pending" && (
                  <div className="mt-5">
                    <TalkInput
                      testid="pulse-correction-input"
                      value={correction}
                      onChange={setCorrection}
                      rows={3}
                      placeholder={mode === "partly" ? "Which part fits, and which part doesn't?" : "Tell me what I'm missing."}
                    />
                    <div className="mt-3 flex gap-2">
                      <button data-testid="pulse-submit-correction-btn" onClick={() => respond(mode)} className="spark-btn-primary">Save correction</button>
                      <button onClick={() => { setMode(null); setCorrection(""); }} className="spark-btn-secondary">Cancel</button>
                    </div>
                  </div>
                )}

                {reflection.status !== "pending" && (
                  <div className="mt-6 flex items-center gap-2 text-sm text-[#8A8A91]">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#2C50A0]" />
                    {reflection.status === "yes" && "Confirmed — added to your Career Compass. If a Gold Thread emerged, you'll see it on your Journey."}
                    {reflection.status === "partly" && "Recalibrated with your correction."}
                    {reflection.status === "no" && "Set aside. Spark won't build on this interpretation."}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            <Link data-testid="pulse-open-compass-btn" to="/compass" className="spark-btn-secondary">Open my Compass</Link>
            <Link data-testid="pulse-continue-journey-btn" to="/part-one" className="spark-btn-primary">Continue my journey</Link>
          </div>
        </div>
      )}
    </div>
  );
}
